import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
/**
 * Persisted collapsed state, keyed by `sidebar:collapsed:<key>` in
 * localStorage. Matches the Electron helper so the same key set stays
 * compatible across shells if they ever share a storage surface.
 */
export function useCollapsed(key, defaultValue = false) {
    const storageKey = `sidebar:collapsed:${key}`;
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            return v === null ? defaultValue : v === "1";
        }
        catch {
            return defaultValue;
        }
    });
    const toggle = () => {
        setCollapsed((c) => {
            const next = !c;
            try {
                localStorage.setItem(storageKey, next ? "1" : "0");
            }
            catch {
                /* storage unavailable — in-session toggle still works */
            }
            return next;
        });
    };
    return [collapsed, toggle];
}
export function Chevron({ collapsed }) {
    // KTK-274 — points right when collapsed, rotates 90° clockwise to point
    // down when open. Matches the .wsec__chev rotation pattern in the
    // web-shell artboards.
    return (_jsx("svg", { width: "8", height: "8", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", style: {
            transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
            transition: "transform 120ms ease",
            flexShrink: 0,
            color: "var(--ink-4)",
        }, children: _jsx("polyline", { points: "9 6 15 12 9 18" }) }));
}
/**
 * Sidebar section with a clickable header that toggles visibility of
 * its body. `storageKey` persists the collapsed state.
 *
 * KTK-274 — header treatment ported from the web-shell artboards:
 * mono 9.5px uppercase label in `--ink-3`, hover lifts to `--ink`,
 * chevron rotates 90° when open. Body padding tightened to match the
 * row heights of the channel/agent lists.
 */
export function CollapsibleSection({ label, storageKey, defaultCollapsed, actions, children, }) {
    const [collapsed, toggle] = useCollapsed(storageKey, defaultCollapsed ?? false);
    const headerStyle = {
        display: "flex",
        alignItems: "center",
        padding: "4px 6px",
    };
    return (_jsxs("section", { style: { padding: "6px 0 4px 6px" }, children: [_jsxs("div", { style: headerStyle, children: [_jsxs("button", { type: "button", onClick: toggle, title: collapsed ? "Expand" : "Collapse", style: {
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "none",
                            border: "none",
                            padding: "2px 4px",
                            cursor: "pointer",
                            color: "var(--ink-3)",
                            fontFamily: "var(--mono)",
                            fontSize: 9.5,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            borderRadius: 4,
                        }, children: [_jsx(Chevron, { collapsed: collapsed }), _jsx("span", { style: {
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }, children: label })] }), actions && _jsx("span", { style: { display: "inline-flex" }, children: actions })] }), !collapsed && (_jsx("div", { style: { display: "flex", flexDirection: "column", padding: "2px 0 4px" }, children: children }))] }));
}
//# sourceMappingURL=collapsible.js.map